import React, { useState } from "react";

const faqs = [
  {
    question: "Is there a free trial?",
    answer:
      "Yes — every new account starts with a 14-day free trial on the Professional plan. No credit card required. You can explore all core features and cancel anytime before the trial ends.",
  },
  {
    question: "What is store credit?",
    answer:
      "Store credit is a digital currency your customers earn and spend exclusively in your Shopify store. It replaces cash refunds, drives repeat purchases, and keeps revenue inside your ecosystem instead of going back to a payment processor.",
  },
  {
    question: "Can I switch between plans?",
    answer:
      "Absolutely. You can upgrade or downgrade at any time from your dashboard. Upgrades take effect immediately; downgrades apply at the end of your current billing cycle. Any unused portion of your current plan is prorated.",
  },
  {
    question: "How does annual billing work?",
    answer:
      "When you choose annual billing you pay for 12 months upfront and receive over 15% off the monthly rate. The charge renews automatically each year unless you cancel before the renewal date.",
  },
  {
    question: "What happens to my customers' store credit if I cancel?",
    answer:
      "Your customers' balances are always visible in Shopify's native gift-card section. If you cancel Pabloo, those balances remain accessible through Shopify directly and customers can still redeem them.",
  },
  {
    question: "Does Pabloo work with multiple Shopify stores?",
    answer:
      "Each Pabloo subscription covers one Shopify store. If you operate multiple stores, you will need a separate subscription for each one. Reach out to us for volume pricing.",
  },
  {
    question: "Which payment methods do you accept?",
    answer:
      "All major credit and debit cards (Visa, Mastercard, Amex, Discover) via Stripe. We do not currently accept PayPal or bank transfers for self-serve subscriptions.",
  },
  {
    question: "Can I get a refund?",
    answer:
      "Every new account starts with a 14-day free trial — no credit card required, no charge until the trial ends. If you decide Pabloo isn't right for you, simply cancel before the trial period closes and you will never be billed.",
  },
  {
    question: "What integrations are included?",
    answer:
      "Pabloo integrates natively with Shopify, Shopify Flow, Klaviyo, and Gorgias on both plans. Premium adds access to our REST API for custom integrations and extended webhook support.",
  },
  {
    question: "Is there a limit on orders or customers?",
    answer:
      "No. Both plans include unlimited orders and unlimited customer accounts. We do not charge per transaction or per customer, so your costs stay flat as your store grows.",
  },
];

function ChevronIcon({ open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left py-5 px-1 gap-4 focus:outline-none group"
      >
        <span className="text-sm font-semibold text-gray-800 group-hover:text-green-700 transition-colors">
          {question}
        </span>
        <ChevronIcon open={open} />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${open ? "max-h-96 pb-5" : "max-h-0"}`}
      >
        <p className="text-sm text-gray-600 leading-relaxed px-1">{answer}</p>
      </div>
    </div>
  );
}

export default function PricingFAQ() {
  const half = Math.ceil(faqs.length / 2);
  const leftColumn = faqs.slice(0, half);
  const rightColumn = faqs.slice(half);

  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-12">
      {/* Heading */}
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently asked questions</h2>
        <p className="text-gray-500 text-base">
          Can't find what you're looking for?{" "}
          <button
            onClick={() => window.$crisp && window.$crisp.push(["do", "chat:open"])}
            className="text-green-700 font-medium hover:underline"
          >
            Talk to our team
          </button>
          .
        </p>
      </div>

      {/* Two-column accordion */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
        <div>
          {leftColumn.map((faq, i) => (
            <FAQItem key={i} question={faq.question} answer={faq.answer} />
          ))}
        </div>
        <div>
          {rightColumn.map((faq, i) => (
            <FAQItem key={i} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    </div>
  );
}
